import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

export const generateDocx = async (
  content: string,
  fileName?: string,
  title?: string,
): Promise<void> => {
  try {
    // Helper to criar TextRuns a partir de uma linha com markdown simples
    const createTextRunsFromMarkdown = (line: string): TextRun[] => {
      const runs: TextRun[] = [];
      const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = pattern.exec(line)) !== null) {
        if (match.index > lastIndex) {
          const plainText = line.substring(lastIndex, match.index);
          if (plainText) {
            runs.push(
              new TextRun({
                text: plainText,
                font: 'Times New Roman',
                size: 24, // 12pt
              }),
            );
          }
        }

        const token = match[0];
        let text = token;
        let bold = false;
        let italics = false;

        if (token.startsWith('**') && token.endsWith('**')) {
          text = token.slice(2, -2);
          bold = true;
        } else if (
          (token.startsWith('*') && token.endsWith('*')) ||
          (token.startsWith('_') && token.endsWith('_'))
        ) {
          text = token.slice(1, -1);
          italics = true;
        }

        if (text) {
          runs.push(
            new TextRun({
              text,
              bold,
              italics,
              font: 'Times New Roman',
              size: 24,
            }),
          );
        }

        lastIndex = pattern.lastIndex;
      }

      if (lastIndex < line.length) {
        const remaining = line.substring(lastIndex);
        if (remaining) {
          runs.push(
            new TextRun({
              text: remaining,
              font: 'Times New Roman',
              size: 24,
            }),
          );
        }
      }

      if (runs.length === 0) {
        runs.push(
          new TextRun({
            text: line,
            font: 'Times New Roman',
            size: 24,
          }),
        );
      }

      return runs;
    };

    const paragraphSpacing = { line: 360 }; // 1.5 linhas

    // Parse markdown content e converter para parágrafos com formatação aproximada ABNT
    const lines = content.split('\n');
    const docParagraphs: Paragraph[] = [];

    for (const rawLine of lines) {
      const line = rawLine.trimEnd();
      const trimmed = line.trim();

      if (!trimmed) {
        docParagraphs.push(
          new Paragraph({
            spacing: paragraphSpacing,
          }),
        );
        continue;
      }

      if (trimmed.startsWith('# ')) {
        docParagraphs.push(
          new Paragraph({
            text: trimmed.substring(2).trim(),
            heading: HeadingLevel.TITLE,
          }),
        );
        continue;
      }

      if (trimmed.startsWith('## ')) {
        docParagraphs.push(
          new Paragraph({
            text: trimmed.substring(3).trim(),
            heading: HeadingLevel.HEADING_1,
          }),
        );
        continue;
      }

      if (trimmed.startsWith('### ')) {
        docParagraphs.push(
          new Paragraph({
            text: trimmed.substring(4).trim(),
            heading: HeadingLevel.HEADING_2,
          }),
        );
        continue;
      }

      // Lista com marcador (- ou *)
      if (/^[-*+]\s+/.test(trimmed)) {
        const itemText = trimmed.replace(/^[-*+]\s+/, '').trim();
        docParagraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: '• ',
                font: 'Times New Roman',
                size: 24,
              }),
              ...createTextRunsFromMarkdown(itemText),
            ],
            spacing: paragraphSpacing,
            alignment: AlignmentType.JUSTIFIED,
            indent: {
              left: 720,
              hanging: 360,
            },
          }),
        );
        continue;
      }

      // Lista numerada simples (1. 2. 3.)
      if (/^\d+[.)]\s+/.test(trimmed)) {
        const itemText = trimmed.replace(/^\d+[.)]\s+/, '').trim();
        docParagraphs.push(
          new Paragraph({
            children: createTextRunsFromMarkdown(itemText),
            spacing: paragraphSpacing,
            alignment: AlignmentType.JUSTIFIED,
            indent: {
              left: 720,
              hanging: 360,
            },
          }),
        );
        continue;
      }

      // Parágrafo normal
      docParagraphs.push(
        new Paragraph({
          children: createTextRunsFromMarkdown(trimmed),
          spacing: paragraphSpacing,
          alignment: AlignmentType.JUSTIFIED,
          indent: {
            firstLine: 720, // ~1,25 cm
          },
        }),
      );
    }

    const doc = new Document({
      styles: {
        default: {
          run: {
            font: 'Times New Roman',
            size: 24, // 12pt
          },
          paragraph: {
            spacing: paragraphSpacing,
          },
        },
      },
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({
              text: title || 'Documento Gerado',
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Gerado em ${new Date().toLocaleDateString('pt-BR')}`,
                  italics: true,
                }),
              ],
              spacing: paragraphSpacing,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: '',
            }),
            ...docParagraphs,
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const finalFileName = fileName || `documento-${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, finalFileName);
  } catch (error) {
    console.error('Erro ao gerar DOCX:', error);
    throw new Error('Falha ao gerar arquivo DOCX. Tente novamente.');
  }
};
